import { useState } from "react";
import { type ProxyRow, api } from "../lib/api.ts";
import { PROXIES_ABOUT } from "../lib/proxies-copy.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Button, InfoBanner, Input, Page, Select, Table } from "../ui/index.ts";

const STATUSES = [
	{ value: "active", label: "active" },
	{ value: "paused", label: "paused" },
	{ value: "retired", label: "retired" },
];

const BLANK = { label: "", protocol: "http", host: "", port: "", country: "", cred_ref: "" };

export function Proxies() {
	const [proxies, reload] = useLoad(() => api.proxies());
	const [form, setForm] = useState(BLANK);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function run(action: () => Promise<unknown>) {
		setBusy(true);
		setError(null);
		try {
			await action();
			reload();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	const set =
		(k: keyof typeof BLANK) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
			setForm({ ...form, [k]: e.target.value });

	async function create(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError(null);
		try {
			await api.createProxy({
				label: form.label.trim(),
				protocol: form.protocol,
				host: form.host.trim(),
				port: Number(form.port),
				country: form.country.trim() || undefined,
				cred_ref: form.cred_ref.trim() || undefined,
			});
			setForm(BLANK);
			reload();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Page title="Proxies" description={PROXIES_ABOUT}>
			{error && <InfoBanner variant="danger" title={error} />}
			{proxies.state === "ready" && proxies.data.truncated && (
				<InfoBanner variant="warning" title="This list is not complete.">
					More rows exist than one read returns, so what you see here is drawn from the newest 1000.
					Nothing is missing from the database — the rest is not read until the read itself is
					paged.
				</InfoBanner>
			)}

			<Table<ProxyRow>
				columns={[
					{
						id: "label",
						header: "Proxy",
						accessor: (p) => (
							<>
								<div className="font-medium text-content">{p.label}</div>
								<div className="font-mono text-xs text-muted">{p.id}</div>
							</>
						),
					},
					{
						id: "endpoint",
						header: "Endpoint",
						accessor: (p) => (
							<span className="font-mono text-xs">{`${p.protocol}://${p.host}:${p.port}`}</span>
						),
					},
					{ id: "country", header: "Country", accessor: (p) => p.country ?? "—" },
					{
						id: "creds",
						header: "Credentials",
						accessor: (p) => (
							<span className="font-mono text-xs text-muted">{p.credRef ?? "not shown"}</span>
						),
					},
					{
						id: "status",
						header: "Status",
						accessor: (p) => (
							<Select
								value={p.status}
								disabled={busy}
								onChange={(e) => void run(() => api.updateProxy(p.id, { status: e.target.value }))}
								options={STATUSES}
							/>
						),
					},
					{
						id: "actions",
						header: "",
						accessor: (p) => (
							<Button
								variant="ghost"
								size="sm"
								disabled={busy}
								onClick={() => void run(() => api.deleteProxy(p.id))}
							>
								Delete
							</Button>
						),
					},
				]}
				data={proxies.state === "ready" ? proxies.data.proxies : []}
				keyExtractor={(p) => p.id}
				failed={proxies.state === "failed"}
				loading={proxies.state === "loading"}
				emptyMessage="No proxies yet. Add the first one below."
			/>

			<form className="mt-6 flex flex-wrap items-end gap-3" onSubmit={create}>
				<Input label="Label" value={form.label} onChange={set("label")} />
				<Select
					label="Protocol"
					value={form.protocol}
					onChange={set("protocol")}
					options={[
						{ value: "http", label: "http" },
						{ value: "https", label: "https" },
						{ value: "socks5", label: "socks5" },
					]}
				/>
				<Input label="Host" value={form.host} onChange={set("host")} />
				<Input label="Port" inputMode="numeric" value={form.port} onChange={set("port")} />
				<Input label="Country" value={form.country} onChange={set("country")} />
				<Input
					label="1Password item"
					helperText="Name only — the secret stays in the vault"
					value={form.cred_ref}
					onChange={set("cred_ref")}
				/>
				<Button
					type="submit"
					loading={busy}
					disabled={!form.label.trim() || !form.host.trim() || !form.port}
				>
					Add proxy
				</Button>
			</form>
		</Page>
	);
}
